import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import MoreIcon from "@mui/icons-material/MoreVert";
import {
  Alert,
  Avatar,
  Menu,
  MenuItem,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Toolbar from "@mui/material/Toolbar";
import { alpha, styled, Theme } from "@mui/material/styles";
import * as React from "react";
import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { NavigationListItems } from "../../NavigationListItems";
import RoutedContent from "./RoutedContent";
import NavigationProgress from "./NavigationProgress";
import useApi from "../../hooks/useApi";
import { useCerberus } from "../../hooks/useCerberus";
import useHeartbeat from "../../hooks/useHeartbeat";
import Loading from "../Widgets/Loading";
import SessionService from "../../services/SessionService";
import { Footer } from "./Footer";
import { RequiredActionsManager } from "../RequiredActions";
import TimezoneDetection from "../TimezoneDetection/TimezoneDetection";
import { useBranding } from "../../branding/BrandingProvider";
import DisplayForRoles from "../Access/DisplayForRoles";
import { PATIENT_RECORD_STAFF_ROLES, Role } from "../Access/Role";
import { audienceForRoles } from "../Access/audience";
import { useSimpleMode } from "../../contexts/SimpleModeContext";
import { withSimpleMode } from "../../theme/simpleMode";
import HeaderPatientSearch from "./HeaderPatientSearch";
import { Wordmark } from "./Wordmark";

// Internal staff who can use the global patient search: exactly who POST /patients/search admits
// (PatientsController's class gate, mirrored by PATIENT_RECORD_STAFF_ROLES). Developer, Auth Team and
// PAC Team are outside it and would only get a 403 dialog.
const PATIENT_SEARCH_ROLES = PATIENT_RECORD_STAFF_ROLES;

const drawerWidth: number = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  background: theme.palette.brand.shell.background,
  color: theme.palette.brand.shell.text,
  backdropFilter: "blur(8px)",
  borderBottom: `1px solid ${theme.palette.brand.border}`,
  boxShadow: "none",
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: open ? drawerWidth : theme.spacing(7),
    minWidth: open ? drawerWidth : theme.spacing(7),
    maxWidth: drawerWidth,
    background: theme.palette.brand.shell.background,
    borderRight: `1px solid ${theme.palette.brand.border}`,
    color: theme.palette.brand.shell.text,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    overflowX: "hidden",
    ...(!open && {
      width: theme.spacing(7),
      minWidth: theme.spacing(7),
      maxWidth: theme.spacing(7),
      overflowX: "hidden",
    }),
  },
}));

const navAccent = (theme: Theme) => theme.palette.brand.accent;

function AppEntryContent() {
  const brand = useBranding();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  // Only truly narrow (phone-width) viewports get the temporary overlay drawer; desktop
  // windows — including narrower ones — keep the collapsible permanent rail. (md was too
  // aggressive: it flipped sub-900px desktop windows into the mobile overlay layout.)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isRegistered, loading, cerberusUser, roles } = useCerberus();
  const api = useApi();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState();
  const [bannerType, setBannerType] = useState();
  const [userInitials, setUserInitials] = useState<string>("");
  const sessionService = SessionService.getInstance();

  useHeartbeat();

  useEffect(() => {
    if (cerberusUser?.firstName && cerberusUser?.lastName) {
      const initials =
        `${cerberusUser.firstName[0]}${cerberusUser.lastName[0]}`.toUpperCase();
      setUserInitials(initials);
    }
  }, [cerberusUser]);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  // On mobile the drawer is a temporary overlay — close it after navigating so it
  // doesn't stay covering the page the user just opened.
  useEffect(() => {
    if (isMobile) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const navigateToAccountSettings = () => {
    handleMenuClose();
    navigate(`/account`);
  };

  const menuId = "primary-search-account-menu";
  const mobileMenuId = "primary-search-account-menu-mobile";

  const handleLogout = async () => {
    try {
      await sessionService.logout();
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout failed:", error);
      // Still clear the session and redirect even if the API call fails
      sessionService.clearSession();
      window.location.href = "/login";
    }
  };

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={navigateToAccountSettings}>My Account</MenuItem>
      <MenuItem onClick={handleLogout}>Logout</MenuItem>
    </Menu>
  );

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={navigateToAccountSettings}>My Account</MenuItem>
      <MenuItem onClick={handleLogout}>Logout</MenuItem>
    </Menu>
  );

  useEffect(() => {
    const getSiteSettings = async () => {
      try {
        const result = await api.get(`/site-settings`);

        if (result.data.showBanner && result.data.bannerMessage) {
          setBannerMessage(result.data.bannerMessage);
          setShowBanner(true);
          setBannerType(result.data.bannerType);
        }
      } catch (error) {}
    };

    // A signed-in account with no role is refused everything but identity and session (it sees the
    // "no roles assigned" card), so asking would only raise a 403 dialog.
    if (roles.length > 0) getSiteSettings();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles.length]);

  if (loading) return <Loading />;

  // Rendered as a full-width strip at the very top of the layout, above the AppBar.
  const siteBanner =
    showBanner && bannerType ? (
      <Alert
        severity={bannerType as "error" | "warning" | "info" | "success"}
        sx={{
          mx: 0,
          mt: 0,
          mb: 0,
          borderRadius: 0,
          fontSize: "1.1rem",
          py: 1.5,
          boxShadow: (theme) => `inset 0 -1px 0 ${theme.palette.divider}`,
          // Theme-derived soft tint + a solid left accent, so the banner reads cleanly in both
          // light and dark instead of muddy hardcoded fills.
          background: (theme) =>
            alpha(
              theme.palette[
                (bannerType || "info") as "error" | "warning" | "info" | "success"
              ].main,
              theme.palette.mode === "dark" ? 0.2 : 0.15,
            ),
          color: "text.primary",
          borderLeft: (theme) =>
            `4px solid ${
              theme.palette[
                (bannerType || "info") as "error" | "warning" | "info" | "success"
              ].main
            }`,
          "& .MuiAlert-icon": {
            fontSize: "1.5rem",
            color: (theme) =>
              theme.palette[
                (bannerType || "info") as "error" | "warning" | "info" | "success"
              ].main,
          },
          "& .MuiAlert-message": {
            color: "inherit",
          },
        }}
      >
        {bannerMessage}
      </Alert>
    ) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <CssBaseline />
      {/* Site banner sits above everything — full width, pushing the AppBar + app down. */}
      {siteBanner}
      {/* Skip link: first tab stop, lets keyboard users bypass the nav on every page. */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          left: 8,
          top: -60,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          px: 2,
          py: 1,
          borderRadius: 1,
          bgcolor: "background.paper",
          color: "primary.main",
          fontWeight: 600,
          textDecoration: "none",
          boxShadow: 3,
          transition: "top 0.2s ease",
          "&:focus": { top: 8 },
        }}
      >
        Skip to main content
      </Box>
      {/* The app row (AppBar + drawer + main). Positioned so the absolute AppBar anchors here,
          directly below the site banner rather than the top of the viewport. */}
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          position: "relative",
          width: "100%",
        }}
      >
        <AppBar position="absolute" open={open && !isMobile}>
        <Toolbar
          sx={{
            pr: "24px",
            minHeight: "64px !important",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isRegistered && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="Expand navigation"
                aria-expanded={open}
                onClick={toggleDrawer}
                sx={{
                  marginRight: 2,
                  ...(open && { display: "none" }),
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Typography
              variant="h6"
              noWrap
              component={RouterLink}
              to="/"
              aria-label={`${brand.shortName} home`}
              sx={{
                // The text wordmark: first word in the brand's lead colour, the rest in its
                // second (palette.brand.wordmark), in the heading face.
                fontFamily: theme.typography.h6.fontFamily,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                textDecoration: "none",
                cursor: "pointer",
                borderRadius: 1,
                "&:focus-visible": {
                  outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
                // Forced-colors / high-contrast: use the system text colour for both halves.
                "@media (forced-colors: active)": {
                  color: "CanvasText",
                  "& span": { color: "CanvasText !important" },
                },
              }}
            >
              <Wordmark name={brand.shortName} />
            </Typography>
          </Box>

          {isRegistered && (
            <>
              <DisplayForRoles roles={PATIENT_SEARCH_ROLES}>
                <HeaderPatientSearch variant="internal" />
              </DisplayForRoles>
              <DisplayForRoles roles={[Role.ExternalProvider]}>
                <HeaderPatientSearch variant="external" />
              </DisplayForRoles>
            </>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{ display: { xs: "none", md: "flex" }, alignItems: "center" }}
            >
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                sx={{
                  color: "inherit",
                }}
              >
                <Avatar
                  src={cerberusUser?.profilePictureUrl || undefined}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: cerberusUser?.profilePictureUrl
                      ? undefined
                      : (theme) => theme.palette.primary.main,
                    color: (theme) => theme.palette.primary.contrastText,
                    border: (theme) => `2px solid ${theme.palette.divider}`,
                    fontWeight: 600,
                  }}
                >
                  {!cerberusUser?.profilePictureUrl && (userInitials || "U")}
                </Avatar>
              </IconButton>
            </Box>

            <Box sx={{ display: { xs: "flex", md: "none" } }}>
              <IconButton
                size="large"
                aria-label="show more"
                aria-controls={mobileMenuId}
                aria-haspopup="true"
                onClick={handleMobileMenuOpen}
                color="inherit"
                sx={{
                  transition: "opacity 0.2s",
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
              >
                <MoreIcon />
              </IconButton>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
      {isRegistered &&
        (() => {
          const drawerContent = (
            <>
              <Toolbar
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  px: [1],
                  minHeight: "64px !important",
                }}
              >
                <IconButton
                  onClick={toggleDrawer}
                  aria-label={isMobile ? "Close navigation" : "Collapse navigation"}
                  aria-expanded={open}
                  sx={{
                    transition: "transform 0.2s",
                    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                    "&:hover": { transform: "scale(1.1)" },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Toolbar>
              <Divider />
              <List
                component="nav"
                aria-label="Main navigation"
                sx={{
                  py: 1,
                  "& .MuiListItemButton-root": {
                    mb: 0.5,
                    "&.Mui-selected": {
                      // Unambiguous current-page indicator: tint + accent bar + bold. TeleHeadache
                      // light marks selection in teal (its guide); everything else in primary.
                      backgroundColor: (t) => alpha(navAccent(t), 0.12),
                      boxShadow: (t) => `inset 3px 0 0 ${navAccent(t)}`,
                      color: "text.primary",
                      "& .MuiListItemIcon-root": { color: (t) => navAccent(t) },
                      "& .MuiListItemText-primary": { fontWeight: 700 },
                      "&:hover": {
                        backgroundColor: (t) => alpha(navAccent(t), 0.18),
                      },
                    },
                    "&:hover": {
                      backgroundColor: (t) => t.palette.action.hover,
                    },
                    "&.Mui-focusVisible": {
                      outline: (t) => `2px solid ${t.palette.primary.main}`,
                      outlineOffset: "-2px",
                    },
                  },
                }}
              >
                <NavigationListItems
                  leftNavOpen={isMobile ? true : open}
                  pathname={pathname}
                />
              </List>
            </>
          );

          // Mobile: temporary overlay drawer (doesn't steal layout width).
          // Desktop: the collapsible permanent mini-drawer.
          return isMobile ? (
            <MuiDrawer
              variant="temporary"
              open={open}
              onClose={toggleDrawer}
              ModalProps={{ keepMounted: true }}
              sx={{
                "& .MuiDrawer-paper": {
                  width: drawerWidth,
                  boxSizing: "border-box",
                },
              }}
            >
              {drawerContent}
            </MuiDrawer>
          ) : (
            <Drawer variant="permanent" open={open}>
              {drawerContent}
            </Drawer>
          );
        })()}
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          backgroundColor: "background.default",
          // The brand's ambient glow (the dark themes' teal/blue radials; "none" in light).
          backgroundImage: (theme) => theme.palette.brand.ambient,
          backgroundAttachment: "fixed",
          flexGrow: 1,
          height: "100%",
          // Anchors the navigation progress bar, which overlays the top of this region.
          position: "relative",
          // The scrollable region is the content Box below (so the footer stays pinned to the
          // bottom instead of scrolling with the page); main itself never scrolls.
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          "&:focus": { outline: "none" },
        }}
      >
        <NavigationProgress />
        <Toolbar />
        <Box
          sx={{
            flex: 1,
            // Load-bearing: pins the footer to the bottom instead of letting it scroll away.
            minHeight: 0,
            overflow: "auto",
          }}
        >
          {/* RoutedContent fills this box and owns the layout inside it (banner, page slot,
              margin containment). This box only has to hand it a definite height to resolve
              against — hence height:100% rather than the min-height it used to carry. */}
          <Box sx={{ height: "100%" }}>
            <RoutedContent />
          </Box>
        </Box>
        <Footer />
      </Box>
      </Box>
      {/* Required Actions Manager - shows dialogs for critical actions */}
      {!loading && <RequiredActionsManager />}
      
      {/* Timezone Detection - prompts users to update timezone if needed */}
      {!loading && <TimezoneDetection />}
    </Box>
  );
}

export default function AppEntry() {
  const { simpleMode } = useSimpleMode();
  const { roles } = useCerberus();
  // Simple/accessible mode enlarges type, spacing, targets and contrast for the PATIENT portal
  // only — clinical and external staff on the same (CerebraLinked) cell keep the dense UI. The
  // nested ThemeProvider merges the override onto the resolved brand+dark theme via createTheme.
  const applySimple = simpleMode && audienceForRoles(roles) === "patient";

  return (
    <ThemeProvider
      theme={(outer) => (applySimple ? withSimpleMode(outer as Theme) : outer)}
    >
      <AppEntryContent />
    </ThemeProvider>
  );
}
